import { defineStore } from 'pinia';
import AssistantSettingsAPI from 'dashboard/api/assistant/settings';
import AssistantProviderCredentialsAPI from 'dashboard/api/assistant/providerCredentials';

export const useAssistantSettingsStore = defineStore('assistantSettings', {
  state: () => ({
    provider: '',
    model: '',
    apiEndpoint: '',
    apiKeyConfigured: false,
    apiKeyHint: '',
    dailyBudgetRub: '',
    copilotModel: '',
    embeddingModel: '',
    embeddingProvider: '',
    embeddingModels: {},
    phoneVerificationMethods: [],
    verificationCallNumber: '',
    providers: [],
    models: {},
    credentials: [],
    // Р78: результат последней проверки связи — { chat: {...}, embeddings: {...} }.
    health: null,
    uiFlags: {
      isFetching: false,
      isUpdating: false,
      isCheckingHealth: false,
    },
  }),

  getters: {
    getUIFlags: state => state.uiFlags,
    getModelsForProvider: state => provider => state.models?.[provider] || [],
  },

  actions: {
    setFromResponse(data = {}) {
      this.provider = data.provider || '';
      this.model = data.model || '';
      this.apiEndpoint = data.api_endpoint || '';
      this.apiKeyConfigured = Boolean(data.api_key_configured);
      this.apiKeyHint = data.api_key_hint || '';
      this.dailyBudgetRub = data.daily_budget_rub ?? '';
      this.copilotModel = data.copilot_model || '';
      this.embeddingModel = data.embedding_model || '';
      this.embeddingProvider = data.embedding_provider || '';
      this.embeddingModels = data.embedding_models || {};
      this.phoneVerificationMethods = data.phone_verification_methods || [];
      this.verificationCallNumber = data.verification_call_number || '';
      this.providers = data.providers || [];
      this.models = data.models || {};
      this.credentials = data.credentials || [];
    },

    async fetch() {
      this.uiFlags.isFetching = true;
      try {
        const response = await AssistantSettingsAPI.get();
        this.setFromResponse(response.data);
      } catch (error) {
        // Ignore error
      } finally {
        this.uiFlags.isFetching = false;
      }
    },

    async update(settings) {
      this.uiFlags.isUpdating = true;
      try {
        const response = await AssistantSettingsAPI.update(settings);
        this.setFromResponse(response.data);
        return response.data;
      } finally {
        this.uiFlags.isUpdating = false;
      }
    },

    // Р78: проверка связи с провайдером. Список моделей для этого не годится —
    // он отдаётся и при нулевом балансе, то есть скрывает главную поломку.
    async healthCheck() {
      this.uiFlags.isCheckingHealth = true;
      try {
        const response = await AssistantSettingsAPI.healthCheck();
        this.health = response.data;
        return response.data;
      } finally {
        this.uiFlags.isCheckingHealth = false;
      }
    },

    // Подключения провайдеров (Р9.2)
    async addCredential(data) {
      const response = await AssistantProviderCredentialsAPI.create(data);
      this.credentials = [...this.credentials, response.data];
      return response.data;
    },

    async updateCredential(id, data) {
      const response = await AssistantProviderCredentialsAPI.update(id, data);
      this.credentials = this.credentials.map(credential =>
        credential.id === id ? response.data : credential
      );
      return response.data;
    },

    async removeCredential(id) {
      await AssistantProviderCredentialsAPI.delete(id);
      this.credentials = this.credentials.filter(
        credential => credential.id !== id
      );
    },

    // Р10 п.1: подтянуть актуальный каталог моделей провайдера по API.
    async syncCredentialModels(id) {
      const response = await AssistantProviderCredentialsAPI.syncModels(id);
      this.credentials = this.credentials.map(credential =>
        credential.id === id ? response.data : credential
      );
      return response.data;
    },

    // Р67: после синка каталог эмбеддинг-моделей на бэкенде обновился —
    // забираем только справочники, не трогая значения формы (иначе
    // несохранённый выбор пользователя сбросился бы под руками).
    async refreshCatalogs() {
      const { data } = await AssistantSettingsAPI.get();
      this.embeddingModels = data.embedding_models || {};
      this.models = data.models || {};
      this.providers = data.providers || [];
      this.credentials = data.credentials || [];
    },
  },
});
